import { useState } from "react";
import { CopyButton, PhoneIcon } from "@signet/ui";
import { CreatorAvatar, type CreatorPhotos } from "@signet/web-kit";
import { useStep } from "./account.tsx";
import { BanModal, banLine, StatusButtons } from "./status.tsx";
import { classLabel, pfpLabel, TYPE_LABEL, type AccountType, type Stock } from "./api.ts";
import { DayTrack } from "./track.tsx";
import { PhoneNote, PhoneNoteButton, RemovedPrompt } from "./notes.tsx";
import { PlanButton, PlannedSlots } from "./plan.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   One phone and its accounts, full size: the phone level of the Accounts tab
   (levels.tsx), where a poster works through what is on the phone in their
   hand. readOnly is someone else's phone: everyone may look, only its poster
   or a manager may tick or change it.
   ────────────────────────────────────────────────────────────────────────── */

export const ended = (a: Stock) => a.status === "banned" || a.status === "suspended";
/**
 * Came off the sheet live, but its link never came across (2026-10-20): it gets one here, no flag needed.
 * Restricted counts too. It is an old status the buttons show as Active, and it had no link button at all:
 * @tacansoci was locked, got back in, and had no way to a link (Ben, 2026-10-27).
 */
export const sheetLinkMissing = (a: Stock) => Boolean(a.importedActive) && (a.step.stage === "linked" || a.step.stage === "restricted") && !a.linkAddedOn;
/** Restricted with a link already: once it's fine again it gets a new one, as after a highlight. */
export const restrictedWithLink = (a: Stock) => a.step.stage === "restricted" && !sheetLinkMissing(a);

export function TypeBadge({ type, count, mini = false }: { type: AccountType; count?: number; mini?: boolean }) {
  return (
    <span className={`igs-type ${type === "aged" ? "aged" : type === "unmarked" ? "unmarked" : "soft"}${mini ? " mini" : ""}`}>
      {TYPE_LABEL[type]}
      {count !== undefined ? <b>{count}</b> : null}
    </span>
  );
}

/** AGED or SR in the top right of a phone, its tile on All, and an account's drawer (Jay, 2026-10-27). An untyped phone has none. */
export function TypeChip({ type }: { type: AccountType }) {
  if (type === "unmarked") return null;
  return <span className={`igs-type igs-type-chip ${type === "aged" ? "aged" : "soft"}`}>{type === "aged" ? "AGED" : "SR"}</span>;
}

export function SetupLine({ a }: { a: Stock }) {
  return (
    <ul className="igs-setup-line" aria-label="Setup">
      <li>{classLabel(a)}</li>
      <li>{pfpLabel(a.pfp)}</li>
      <li>{a.warmupSkipped ? "No warm-up" : `${a.warmup}-day warm-up`}</li>
    </ul>
  );
}

export function AccountBlock({ a, photos, onOpen, onLink, showPoster, readOnly = false }: { a: Stock; photos: CreatorPhotos; onOpen: () => void; onLink: () => void; showPoster: boolean; readOnly?: boolean }) {
  const step = useStep();
  const after = (a.linkAddedOn || a.step.stage === "linked" || a.step.stage === "flagged" || a.step.stage === "highlight" || a.step.stage === "restricted") && !ended(a);
  // A banned account stays on its phone in red, with only what is left to do with it (Jay, 2026-10-12).
  if (ended(a)) {
    return (
      <article className="igs-account banned" aria-label={`@${a.username}, ${banLine(a)}`}>
        <header className="igs-account-head">
          {a.creator ? <CreatorAvatar name={a.creator} src={photos(a.creator)} size={34} /> : null}
          <div className="igs-account-name">
            <h3>@{a.username}</h3>
            <p className="igs-banned-line">{banLine(a)}</p>
            {a.banReason === "three_reasons" && a.lastNote?.note ? <p className="igs-appeal-last">{a.lastNote.note}</p> : null}
          </div>
          <button type="button" className="secondary-button igs-open" onClick={onOpen}>Details</button>
        </header>
        <RemovedPrompt a={a} canAct={!readOnly} />
        {a.note ? <NoteLine note={a.note} /> : null}
        {!readOnly ? <StatusButtons a={a} /> : null}
      </article>
    );
  }
  return (
    <article className="igs-account" aria-label={`@${a.username}`}>
      <header className="igs-account-head">
        {a.creator ? <CreatorAvatar name={a.creator} src={photos(a.creator)} size={34} /> : null}
        <div className="igs-account-name">
          <h3>@{a.username}</h3>
          <p>
            {a.creator ?? "No creator"}
            {showPoster && a.posterName ? `, ${a.posterName}'s phone` : ""}
          </p>
        </div>
        <button type="button" className="secondary-button igs-open" onClick={onOpen}>
          {readOnly ? "Details" : "Login and details"}
        </button>
      </header>
      {!readOnly ? <StatusButtons a={a} /> : null}
      <SetupLine a={a} />
      {a.bio ? (
        <p className="igs-bio-line">
          <span>Bio</span>
          <CopyButton value={a.bio} label="bio" className="igs-copy">{a.bio}</CopyButton>
        </p>
      ) : null}
      {a.note ? <NoteLine note={a.note} /> : null}
      {a.missingSince ? <MissingAlert a={a} readOnly={readOnly} /> : null}
      {/* Not posting: nothing to tick until someone picks it up again. */}
      <DayTrack a={a} onLink={onLink} readOnly={readOnly || a.step.stage === "paused"} />
      {a.linkUrl && !ended(a) ? (
        <p className="igs-bio-line">
          <span>Link</span>
          <CopyButton value={a.linkUrl} label="link" className="igs-copy">{a.linkUrl}</CopyButton>
        </p>
      ) : null}
      {after ? (
        <div className="igs-after">
          <p>
            <strong>{a.step.title}.</strong> {a.step.detail}
          </p>
          {!readOnly && sheetLinkMissing(a) ? (
            <button type="button" className="button brass" onClick={onLink}>
              Make its link
            </button>
          ) : null}
          {!readOnly && a.step.stage === "linked" && !sheetLinkMissing(a) ? (
            <>
              <button type="button" className="secondary-button" disabled={step.isPending} onClick={() => step.mutate({ id: a.id, step: "link_flagged" })}>
                Instagram flagged the link
              </button>
              {/* Always a way to a link (Ben, 2026-10-27): an account that was locked and got back in may have lost
                  its link with no flag to report. A new link replaces the old one and counts no flag. */}
              <button type="button" className="text-button" onClick={onLink}>
                Make a new link
              </button>
            </>
          ) : null}
          {!readOnly && restrictedWithLink(a) ? (
            <button type="button" className="button brass" onClick={onLink}>
              Make a new link
            </button>
          ) : null}
          {!readOnly && a.step.stage === "flagged" ? (
            <button type="button" className="button brass" disabled={step.isPending} onClick={() => step.mutate({ id: a.id, step: "highlight" })}>
              Moved it to a highlight
            </button>
          ) : null}
          {!readOnly && a.step.stage === "highlight" ? (
            <button type="button" className="button brass" onClick={onLink}>
              Link works again: make a new link
            </button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

/** The account's note on its card; it is written in the drawer. */
function NoteLine({ note }: { note: string }) {
  return (
    <p className="igs-bio-line igs-notebox-line">
      <span>Note</span>
      <span className="igs-notebox-text">{note}</span>
    </p>
  );
}

/**
 * Instagram can't find it: someone looks on the phone. Banned asks which kind
 * at once; the 2026-10-12 appeal-first wait went on 2026-10-15, because
 * connected bans are never appealed.
 */
export function MissingAlert({ a, readOnly }: { a: Stock; readOnly: boolean }) {
  const step = useStep();
  const [banning, setBanning] = useState(false);
  return (
    <div className="igs-alert igs-missing">
      <p>
        <strong>Instagram can't find this account.</strong> Check it on the phone.
      </p>
      {!readOnly ? (
        <div className="igs-missing-actions">
          <button type="button" className="button danger" disabled={step.isPending} onClick={() => setBanning(true)}>Banned</button>
          <button type="button" className="secondary-button" disabled={step.isPending} onClick={() => step.mutate({ id: a.id, step: "active" })}>It's fine</button>
        </div>
      ) : null}
      <BanModal account={banning ? a : null} onClose={() => setBanning(false)} />
    </div>
  );
}

export function PhoneCard({ label, type, poster, owner, note, plan = [], manages = false, accounts, photos, onOpen, onLink, showPoster, canAct }: { label: string; type: AccountType; poster: string; owner: string | null; note: string | null; plan?: string[]; manages?: boolean; accounts: Stock[]; photos: CreatorPhotos; onOpen: (id: string) => void; onLink: (id: string) => void; showPoster: boolean; canAct: (a: Pick<Stock, "poster">) => boolean }) {
  const live = accounts.filter((a) => !ended(a));
  const gone = accounts.filter(ended);
  const [noting, setNoting] = useState(false);
  const mayNote = canAct({ poster });
  return (
    <section className={`igs-phone ${type === "aged" ? "aged" : type === "unmarked" ? "unmarked" : "soft"}`} aria-label={`Phone ${label}`}>
      <header className="igs-phone-head">
        <PhoneIcon size={18} />
        <h2>
          Phone <span>{label}</span>
        </h2>
        {type === "unmarked" ? <TypeBadge type={type} /> : null}
        {mayNote ? <PhoneNoteButton phone={label} note={note} onClick={() => setNoting(true)} /> : null}
        <p>
          {live.length} live {live.length === 1 ? "account" : "accounts"}
          {showPoster && owner ? `, ${owner}` : ""}
        </p>
        <TypeChip type={type} />
      </header>
      <PhoneNote poster={poster} phone={label} note={note} canEdit={mayNote} editing={noting} onEditing={setNoting} />
      {[...live, ...gone].map((a) => (
        <AccountBlock key={a.id} a={a} photos={photos} onOpen={() => onOpen(a.id)} onLink={() => onLink(a.id)} showPoster={false} readOnly={!canAct(a)} />
      ))}
      <PlannedSlots g={{ poster, label, plan, live }} photos={photos} size={34} />
      {manages ? <PlanButton g={{ poster, label, plan, live }} photos={photos} /> : null}
    </section>
  );
}

